/**
 * Widget persistence: cloud API (primary), localStorage (fallback when API is down).
 */

import {
  deleteWidgetFromApi,
  fetchWidgetById,
  fetchWidgets,
  saveWidgetToApi,
} from '../api/widgets';

const STORAGE_KEY = 'scorewidget_saved_widgets';
const MIGRATED_KEY = 'scorewidget_migrated_to_cloud';

export function createWidgetId() {
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function readLocalAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalAll(widgets) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
}

export function listSavedWidgetsLocal() {
  return readLocalAll().sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function getSavedWidgetLocal(id) {
  return readLocalAll().find((w) => w.id === id) ?? null;
}

function saveWidgetLocal(id, config) {
  const widgets = readLocalAll();
  const now = new Date().toISOString();
  const existingIndex = widgets.findIndex((w) => w.id === id);
  const entry = { id, config: { ...config }, updatedAt: now };

  if (existingIndex >= 0) widgets[existingIndex] = entry;
  else widgets.push(entry);

  writeLocalAll(widgets);
  return entry;
}

/** Load widgets from API; migrate localStorage once if cloud was empty. */
export async function listSavedWidgets() {
  try {
    let widgets = await fetchWidgets();
    if (widgets.length === 0 && !localStorage.getItem(MIGRATED_KEY)) {
      const local = listSavedWidgetsLocal();
      if (local.length > 0) {
        for (const w of local) {
          await saveWidgetToApi(w.id, w.config);
        }
        localStorage.setItem(MIGRATED_KEY, '1');
        widgets = await fetchWidgets();
      }
    }
    return widgets;
  } catch {
    return listSavedWidgetsLocal();
  }
}

export async function getSavedWidget(id) {
  try {
    return await fetchWidgetById(id);
  } catch (e) {
    if (e.status === 404) return null;
    return getSavedWidgetLocal(id);
  }
}

export async function saveWidget(id, config) {
  try {
    const widget = await saveWidgetToApi(id, config);
    const savedConfig = widget.config ?? config;
    saveWidgetLocal(id, savedConfig);
    return { ...widget, config: savedConfig };
  } catch (e) {
    if (e.code === 'WIDGET_LIMIT') throw e;
    return saveWidgetLocal(id, config);
  }
}

export async function deleteSavedWidget(id) {
  try {
    await deleteWidgetFromApi(id);
  } catch {
    /* fallback */
  }
  const widgets = readLocalAll().filter((w) => w.id !== id);
  writeLocalAll(widgets);
}

export function canSaveWidget() {
  return true;
}
